/**
 * Hold a combat mutation in the pending UI state until React commits a token
 * snapshot at the acknowledged server revision (or a later revision). This
 * closes the ACK-before-render gap without guessing from local timestamps.
 * date_of_change: 2026-08-31
 */
export const createAuthoritativeRevisionBarrier = ({ timeoutMs = 15000, onPendingChange = () => {} } = {}) => {
  let appliedRevision = 0;
  let disposed = false;
  const waiters = new Set();

  const notifyPending = () => onPendingChange(waiters.size);

  const markApplied = (revision) => {
    const normalizedRevision = Number(revision);
    if (!Number.isSafeInteger(normalizedRevision) || normalizedRevision < 0) {
      throw new Error(`Authoritative state revision must be a non-negative safe integer; received ${String(revision)}.`);
    }
    if (normalizedRevision <= appliedRevision) return appliedRevision;

    appliedRevision = normalizedRevision;
    [...waiters].forEach((waiter) => {
      if (waiter.revision > appliedRevision) return;
      clearTimeout(waiter.timer);
      waiters.delete(waiter);
      waiter.resolve(appliedRevision);
    });
    notifyPending();
    return appliedRevision;
  };

  const waitFor = (revision) => {
    const normalizedRevision = Number(revision);
    if (!Number.isSafeInteger(normalizedRevision) || normalizedRevision < 0) {
      return Promise.reject(new Error(`Mutation acknowledgement is missing a valid revision; received ${String(revision)}.`));
    }
    if (disposed) return Promise.reject(new Error('Authoritative revision barrier is no longer active.'));
    if (normalizedRevision <= appliedRevision) return Promise.resolve(appliedRevision);

    return new Promise((resolve, reject) => {
      const waiter = { revision: normalizedRevision, resolve, reject, timer: null };
      waiter.timer = setTimeout(() => {
        waiters.delete(waiter);
        notifyPending();
        const error = new Error(`Authoritative revision ${normalizedRevision} was acknowledged but not applied to BattleRoom tokens.`);
        error.code = 'AUTHORITATIVE_PROJECTION_TIMEOUT';
        reject(error);
      }, timeoutMs);
      waiters.add(waiter);
      notifyPending();
    });
  };

  const dispose = () => {
    disposed = true;
    [...waiters].forEach((waiter) => {
      clearTimeout(waiter.timer);
      waiter.reject(new Error('Authoritative revision barrier was disposed before the snapshot applied.'));
    });
    waiters.clear();
    notifyPending();
  };

  return {
    markApplied,
    waitFor,
    dispose,
    getAppliedRevision: () => appliedRevision,
    getPendingCount: () => waiters.size
  };
};

export const reconcileAuthoritativeTokenSnapshot = ({ previousTokens, incomingTokens, revision }) => {
  const normalizedRevision = Number(revision);
  if (!Number.isSafeInteger(normalizedRevision) || normalizedRevision < 0) {
    throw new Error(`Authoritative token snapshot is missing a valid revision; received ${String(revision)}.`);
  }
  if (!Array.isArray(previousTokens) || !Array.isArray(incomingTokens)) {
    throw new Error('Authoritative token reconciliation requires previousTokens and incomingTokens arrays.');
  }

  // The server revision is the mutable-session source of truth. Never retain
  // a locally newer mechanical object over a versioned HP/damage snapshot.
  // date_of_change: 2026-08-31
  return incomingTokens;
};
